/**
 * Framework utility for getting API configuration across the application
 * Provides consistent base URL resolution with proper fallback chain
 */

// Debug logging utility - only logs in development mode
const DEBUG = process.env.NODE_ENV === 'development';
const debugLog = (message: string, ...args: any[]) => {
  if (DEBUG) {
    console.log(`[ApiConfig] ${message}`, ...args);
  }
};

/**
 * Get the base URL for API calls using the framework's standard resolution chain
 * 
 * Resolution order:
 * 1. OpenAPI configuration (set by AppDataContextProvider)
 * 2. Session storage (selectedEnvUrl)
 * 3. Environment variable (REACT_APP_API_URL)
 * 4. Development fallback (localhost:5002)
 */
export const getBaseUrl = (): string => {
  // Priority 1: Try to get from OpenAPI configuration first (preferred)
  // This is set by the AppDataContextProvider when environment is selected
  if (typeof window !== 'undefined' && (window as any).OpenAPI?.BASE) {
    const openApiUrl = (window as any).OpenAPI.BASE;
    debugLog('Using OpenAPI BASE URL:', openApiUrl);
    return openApiUrl;
  }
  
  // Priority 2: Fallback to sessionStorage if OpenAPI config not available
  // This persists the selected environment across page reloads
  const sessionUrl = sessionStorage.getItem('selectedEnvUrl');
  if (sessionUrl) {
    return sessionUrl;
  }
  
  // Priority 3: Check for environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Priority 4: Final fallback for development
  const fallbackUrl = 'http://localhost:5002';
  return fallbackUrl;
};

/**
 * Get the base URL with a specific path appended
 */
export const getApiUrl = (path: string): string => {
  const baseUrl = getBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

/**
 * Check if we're using a development/localhost URL
 */
export const isDevelopmentUrl = (): boolean => {
  const baseUrl = getBaseUrl();
  return baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
};

/**
 * Get environment information from the base URL
 */
export const getEnvironmentInfo = (): {
  baseUrl: string;
  isDevelopment: boolean;
  environment: string;
} => {
  const baseUrl = getBaseUrl();
  const isDev = isDevelopmentUrl();
  
  let environment = 'production';
  if (isDev) {
    environment = 'development';
  } else if (baseUrl.includes('staging') || baseUrl.includes('test')) {
    environment = 'staging';
  }
  
  return {
    baseUrl,
    isDevelopment: isDev,
    environment
  };
};

/**
 * Create a fetch wrapper that automatically uses the correct base URL
 */
export const createApiClient = () => {
  const baseUrl = getBaseUrl();
  
  return {
    get: (path: string, options?: RequestInit) => 
      fetch(getApiUrl(path), { ...options, method: 'GET' }),
    
    post: (path: string, data?: any, options?: RequestInit) =>
      fetch(getApiUrl(path), {
        ...options,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers
        },
        body: data ? JSON.stringify(data) : undefined
      }),
    
    put: (path: string, data?: any, options?: RequestInit) =>
      fetch(getApiUrl(path), {
        ...options,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers
        },
        body: data ? JSON.stringify(data) : undefined
      }),
    
    delete: (path: string, options?: RequestInit) =>
      fetch(getApiUrl(path), { ...options, method: 'DELETE' }),
      
    baseUrl
  };
};
